import { signal } from '@preact/signals';
import type { Message } from '../types/message';
import { apiFetch } from '../services/api';
import { showToast } from './ui.store';

export const messagesSignal = signal<Message[]>([]);
export const isSyncingHistorySignal = signal<boolean>(false);
export const hasMoreLocalMessagesSignal = signal<boolean>(true);
export const isLoadingOlderMessagesSignal = signal<boolean>(false);
export const noMoreCloudMessagesSignal = signal<boolean>(false);

export function appendMessage(msg: Message) {
  const exists = messagesSignal.value.some(m => m.message_id === msg.message_id);
  if (!exists) {
    messagesSignal.value = [...messagesSignal.value, msg];
  }
}

export function prependMessages(olderMessages: Message[]) {
  // Avoid duplicate messages by message_id or id
  const existingIds = new Set(messagesSignal.value.map(m => m.id || m.message_id));
  const newOlder = olderMessages.filter(m => !existingIds.has(m.id || m.message_id));
  if (newOlder.length > 0) {
    messagesSignal.value = [...newOlder, ...messagesSignal.value];
  }
}

export function updateMessageExtractedText(messageId: string, extractedText: string) {
  messagesSignal.value = messagesSignal.value.map(m => {
    if (m.message_id === messageId) {
      return { ...m, extracted_text: extractedText };
    }
    return m;
  });
}

export function updateMessageReaction(targetMessageId: string, reaction: any) {
  if (!targetMessageId || !reaction) return;

  messagesSignal.value = messagesSignal.value.map(m => {
    if (m.message_id === targetMessageId) {
      const currentReactions = m.reactions || [];
      const cleanTargetJid = (reaction.sender_jid || '').split('@')[0].split(':')[0].replace(/\D/g, '');

      const isMatchingReaction = (r: any) => {
        if (r.reaction_id && reaction.reaction_id && String(r.reaction_id) === String(reaction.reaction_id)) {
          return true;
        }
        if (r.sender_jid && reaction.sender_jid) {
          if (r.sender_jid === reaction.sender_jid) return true;
          const cleanRJid = r.sender_jid.split('@')[0].split(':')[0].replace(/\D/g, '');
          if (cleanRJid && cleanTargetJid && cleanRJid === cleanTargetJid) return true;
        }
        return false;
      };

      let updated: any[];
      if (!reaction.emoji) {
        updated = currentReactions.filter(r => !isMatchingReaction(r));
      } else {
        const existingIdx = currentReactions.findIndex(r => isMatchingReaction(r));
        if (existingIdx !== -1) {
          updated = [...currentReactions];
          updated[existingIdx] = reaction;
        } else {
          updated = [...currentReactions, reaction];
        }
      }
      return { ...m, reactions: updated };
    }
    return m;
  });
}

export async function loadMoreLocalMessages(phone: string): Promise<number> {
  if (isLoadingOlderMessagesSignal.value || !hasMoreLocalMessagesSignal.value) return 0;
  const currentMsgs = messagesSignal.value;
  if (currentMsgs.length === 0) return 0;

  const firstMsg = currentMsgs[0];
  if (!firstMsg || !firstMsg.id) return 0;

  isLoadingOlderMessagesSignal.value = true;
  try {
    const older = await apiFetch<Message[]>(`/messages/${phone}?beforeId=${firstMsg.id}&limit=50`);
    if (older && older.length > 0) {
      prependMessages(older);
      if (older.length < 50) {
        hasMoreLocalMessagesSignal.value = false;
      }
      return older.length;
    } else {
      hasMoreLocalMessagesSignal.value = false;
      return 0;
    }
  } catch (err) {
    console.error('[MessagesStore] Erro ao carregar mensagens locais antigas:', err);
    return 0;
  } finally {
    isLoadingOlderMessagesSignal.value = false;
  }
}

export async function syncEvolutionHistory(phone: string): Promise<void> {
  if (isSyncingHistorySignal.value) return;
  isSyncingHistorySignal.value = true;
  try {
    await apiFetch(`/contacts/${phone}/sync-history`, { method: 'POST' });
    const currentMsgs = messagesSignal.value;
    const firstMsg = currentMsgs.length > 0 ? currentMsgs[0] : null;
    const queryStr = firstMsg && firstMsg.id ? `?beforeId=${firstMsg.id}&limit=50` : '?limit=50';
    const newlySynced = await apiFetch<Message[]>(`/messages/${phone}${queryStr}`);
    
    if (newlySynced && newlySynced.length > 0) {
      prependMessages(newlySynced);
      hasMoreLocalMessagesSignal.value = true;
      noMoreCloudMessagesSignal.value = false;
      showToast(`${newlySynced.length} mensagem(ns) antiga(s) sincronizada(s) da nuvem!`, 'success');
    } else {
      noMoreCloudMessagesSignal.value = true;
      showToast('Nenhuma mensagem antiga adicional foi encontrada no WhatsApp.', 'info');
    }
  } catch (err: any) {
    console.error('[MessagesStore] Erro ao sincronizar histórico com a Evolution API:', err);
    showToast(`Erro ao sincronizar com o WhatsApp: ${err.message}`, 'error');
  } finally {
    isSyncingHistorySignal.value = false;
  }
}
